/** AUTH_METHODS_CONFIG control: show the configured Auth0 methods and edit them in a
 *  dialog that reuses the create-wizard AuthSection (staff + mobile-client connections).
 *
 *  Access groups are referenced by codename (portable across the wizard + preflight);
 *  the backend resolves codes ↔ ids. Reads the current methods (already in editor shape)
 *  and the available access-group codes from item.detail.
 */
import { Box, Button, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { PreflightItem } from '@/api/preflight';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';

import { AuthSection } from './AuthSection';
import type { AuthMethodInput } from '../wizard/types';

function parseMethods(raw: unknown): AuthMethodInput[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((m) => {
    const o = (m && typeof m === 'object' ? m : {}) as Record<string, unknown>;
    return {
      name: typeof o.name === 'string' ? o.name : '',
      domain: typeof o.domain === 'string' ? o.domain : '',
      client_id: typeof o.client_id === 'string' ? o.client_id : '',
      allow_signup: Boolean(o.allow_signup),
      allowed_domains: Array.isArray(o.allowed_domains)
        ? o.allowed_domains.filter((x): x is string => typeof x === 'string')
        : [],
      access_group_codes: Array.isArray(o.access_group_codes)
        ? o.access_group_codes.filter((x): x is string => typeof x === 'string')
        : [],
    };
  });
}

function summary(methods: AuthMethodInput[]): string {
  return methods.length ? methods.map((m) => m.name || m.domain || '?').join(', ') : 'none';
}

export function AuthMethodsControl({
  item,
  topId,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const [open, setOpen] = useState(false);
  const [staff, setStaff] = useState<AuthMethodInput[]>([]);
  const [client, setClient] = useState<AuthMethodInput[]>([]);

  const groupCodes = Array.isArray(item.detail.available_access_group_codes)
    ? item.detail.available_access_group_codes.filter((x): x is string => typeof x === 'string')
    : [];
  const persistedStaff = parseMethods(item.detail.auth_methods);
  const persistedClient = parseMethods(item.detail.client_auth_methods);

  const save = useMutation({
    mutationFn: () =>
      items.saveAuthMethods(topId, item.id, { auth_methods: staff, client_auth_methods: client }),
    onSuccess: () => {
      snackbar.show('Auth methods saved.', 'success');
      setOpen(false);
      onChanged();
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  function openDialog() {
    // Seed the editor from the current persisted methods each time it opens.
    setStaff(parseMethods(item.detail.auth_methods));
    setClient(parseMethods(item.detail.client_auth_methods));
    setOpen(true);
  }

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={0.5}>
        <Typography variant="body2" color="text.secondary">
          Staff Auth0: {summary(persistedStaff)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Client Auth0: {summary(persistedClient)}
        </Typography>
        {canManage && (
          <Box>
            <Button size="small" variant="outlined" sx={{ mt: 0.5 }} onClick={openDialog}>
              Configure Auth0
            </Button>
          </Box>
        )}
      </Stack>

      <AppDialog
        open={open}
        onClose={() => setOpen(false)}
        title="Configure Auth0 methods"
        size="md"
        disableBackdropClose
        actions={
          <>
            <Button onClick={() => setOpen(false)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>
              Save
            </Button>
          </>
        }
      >
        <Stack spacing={1} sx={{ pt: 1 }}>
          <AuthSection label="Dashboard (staff Auth0)" methods={staff} onUpdate={setStaff} groupCodes={groupCodes} />
          <AuthSection
            label="Mobile clients (guest/staff Auth0)"
            methods={client}
            onUpdate={setClient}
            groupCodes={groupCodes}
          />
        </Stack>
      </AppDialog>
    </Box>
  );
}
